/**
 * L2/DH Approval & Prioritization (Beta-1 plan P2-11 / P2-12 / P2-14 / P2-36; Tim's D06 image 7,
 * D03 §C.7). The card shows title · ID · NWT · type/subtype · REPS, the entrant → L1 → DREW severity
 * chips, the L1/FLS Proposed REF Action with a green APPROVED toggle, an optional Revised REF
 * Actions/Notes field, then PRIORITIZATION (confirmed severity, priority P0–P3 with a due date for
 * P0, owner incl. a person) and the two buttons Return to L1/FLS · Confirm & Assign Priority.
 * The business-impact slider is gone (Tim).
 */
import React, { useMemo, useState } from 'react';
import { Box, Card, CardContent, Typography, Button, Chip, Stack, Divider, TextField, MenuItem, Alert, Grid, CircularProgress, ToggleButtonGroup, ToggleButton, Switch, FormControlLabel } from '@mui/material';
import GavelIcon from '@mui/icons-material/Gavel';
import LowPriorityIcon from '@mui/icons-material/LowPriority';
import CheckIcon from '@mui/icons-material/Check';
import ReplayIcon from '@mui/icons-material/Replay';
import CloseIcon from '@mui/icons-material/Close';
import { SEVERITY_LEVELS, refActionLabel, CONDITION_STATUS } from '../../utils/trlConstants';
import { useTRLs } from '../../hooks/useTRLs';
import { useConditionViewer } from '../../hooks/useConditionViewer';
import { dhConfirmTRL, dhReturnToL1, dhRejectTRL, approveResolution, returnResolution, reviseDecision, PRIORITIES } from '../../../../services/trlService';
import { triggerPatternDetection } from '../../../../services/n8nService';
import { needsL2Action, isL2For, canApproveResolution, canRevise, isHiddenForViewer, isSingleStep } from '../../utils/conditionWorkflow';
import { ConditionHeader, SeverityChips, sevColor } from '../../components/conditions/ConditionHeader';
import { NoteDialog, OwnerFields, InlineNotice, ownerNameFor } from '../../components/conditions/ConditionDialogs';
import { notifyConditionEvent } from '../../../../services/conditionNotificationService';

function SeverityCalibration() {
  const viewer = useConditionViewer();
  const { trls, loading } = useTRLs({ limit: 300 });
  const [tab, setTab] = useState('queue');
  const [forms, setForms] = useState({});
  const [busyId, setBusyId] = useState(null);
  const [notice, setNotice] = useState(null);
  const [dialog, setDialog] = useState(null);

  const visible = useMemo(() => trls.filter((t) => !isHiddenForViewer(t, viewer)), [trls, viewer]);
  const queue = visible.filter((t) => t.status === CONDITION_STATUS.under_review && needsL2Action(t, viewer, viewer.routing));
  const resolutions = visible.filter((t) => t.status === CONDITION_STATUS.resolution_pending && canApproveResolution(t, viewer, viewer.routing) === 'l2');
  const history = visible.filter((t) => ['approved', 'resolved', 'rejected'].includes(t.status) && (isL2For(t, viewer, viewer.routing) || viewer.isAdmin)).slice(0, 40);

  const formOf = (t) =>
    forms[t.id] || {
      sev: t.severityDH || t.severityFLS || t.severityITM || 'II',
      priority: t.priority || 'P2',
      dueDate: t.dueDate || '',
      refApproved: t.l2?.refActionApproved ?? true,
      revised: t.l2?.revisedRefNotes || '',
      owner: { ownerDeptId: t.ownerDeptId || '', ownerNwtId: t.ownerNwtId || '', ownerUid: t.ownerUid || '' },
    };
  const setForm = (t, patch) => setForms((f) => ({ ...f, [t.id]: { ...formOf(t), ...(f[t.id] || {}), ...patch } }));
  const actor = { by: viewer.uid, byName: viewer.name, actingFor: viewer.actingFor };

  const run = async (id, fn, success) => {
    setBusyId(id);
    setNotice(null);
    try {
      await fn();
      if (success) setNotice({ severity: 'success', message: success });
      setDialog(null);
    } catch (err) {
      console.error(err);
      setNotice({ severity: 'error', message: err.message || 'Something went wrong. Please try again.' });
    } finally {
      setBusyId(null);
    }
  };

  const confirm = (t) => {
    const f = formOf(t);
    return run(
      t.id,
      async () => {
        await dhConfirmTRL(t.id, {
          severityDH: f.sev,
          priority: f.priority,
          dueDate: f.priority === 'P0' ? f.dueDate || null : null,
          refActionApproved: f.refApproved,
          revisedRefNotes: f.revised,
          ownerDeptId: f.owner.ownerDeptId || null,
          ownerNwtId: f.owner.ownerNwtId || null,
          ownerUid: f.owner.ownerUid || null,
          ownerName: f.owner.ownerUid ? ownerNameFor(viewer.members, f.owner.ownerUid) : '',
          confirmedBy: viewer.uid,
          confirmedByName: viewer.name,
          confirmedEnteredBy: viewer.keyboardUid,
          actingFor: viewer.actingFor,
        });
        // Workflow 5: Pattern Detection now that the condition is fully approved
        triggerPatternDetection(t.id, { personaId: viewer.role, deptId: t.deptId, siteId: t.siteId });
        notifyConditionEvent('l2_approved', t, viewer, { after: { ownerDeptId: f.owner.ownerDeptId || null, ownerUid: f.owner.ownerUid || null }, actedAs: 'approved an OE Condition' });
      },
      'Confirmed — the condition is Approved/Active and in the owner\'s list.'
    );
  };

  const l1Ref = (t) => t.l1?.refAction || (t.refActions || []).find((a) => !a.level || a.level === 'l1')?.type || null;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <GavelIcon sx={{ mr: 1, color: 'primary.main', fontSize: 32 }} />
        <Typography variant="h4">L2/DH Approval & Prioritization</Typography>
      </Box>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
        L1/FLS-verified OE Conditions awaiting your review and confirmation of Proposed REF Actions and Severity Level, as well as assignment of Resolution Scheduling/Priority.
      </Typography>
      <ToggleButtonGroup value={tab} exclusive onChange={(_, v) => v && setTab(v)} size="small" sx={{ mb: 2 }}>
        <ToggleButton value="queue">Awaiting my approval ({queue.length})</ToggleButton>
        <ToggleButton value="resolutions">Resolutions to approve ({resolutions.length})</ToggleButton>
        <ToggleButton value="history">My decisions ({history.length})</ToggleButton>
      </ToggleButtonGroup>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
        Use <strong>Return to L1/FLS</strong> only when the L1 reviewer must see your revisions; otherwise <strong>Confirm</strong> (D03 §C.7.2).
      </Typography>

      <InlineNotice notice={notice} onClose={() => setNotice(null)} />

      {tab === 'queue' && (
        <>
          {loading && queue.length === 0 && <Alert severity="info">Loading…</Alert>}
          {!loading && queue.length === 0 && <Alert severity="success">All caught up — no OE Conditions awaiting L2/DH approval.</Alert>}
          <Stack spacing={2}>
            {queue.map((t) => {
              const f = formOf(t);
              const busy = busyId === t.id;
              const ref = l1Ref(t);
              return (
                <Card key={t.id} variant="outlined">
                  <CardContent>
                    <ConditionHeader condition={t} org={viewer.org} showStatus={false} />
                    <Box sx={{ mt: 1, mb: 1.5 }}>
                      <SeverityChips condition={t} />
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>
                      {t.description}
                    </Typography>

                    <Box sx={{ mb: 2, p: 1.5, border: 1, borderColor: 'divider', borderRadius: 2 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                        L1 Review/FLS Proposed REF Actions
                      </Typography>
                      <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
                        {ref ? (
                          <Chip label={`${refActionLabel(ref).toUpperCase()}${t.l1?.notes ? `: ${t.l1.notes}` : ''}`} size="small" variant="outlined" sx={{ fontWeight: 700 }} />
                        ) : (
                          <Typography variant="body2" color="text.secondary">No REF action proposed at L1.</Typography>
                        )}
                        <Box sx={{ flexGrow: 1 }} />
                        <FormControlLabel
                          control={<Switch color="success" checked={Boolean(f.refApproved)} onChange={(e) => setForm(t, { refApproved: e.target.checked })} />}
                          label={<Chip label="APPROVED" size="small" color={f.refApproved ? 'success' : 'default'} sx={{ fontWeight: 800 }} />}
                        />
                      </Stack>
                      <TextField
                        size="small"
                        fullWidth
                        multiline
                        minRows={2}
                        sx={{ mt: 1.5 }}
                        label="Revised REF Actions / Notes (optional)"
                        value={f.revised}
                        onChange={(e) => setForm(t, { revised: e.target.value })}
                      />
                    </Box>

                    <Divider sx={{ mb: 2 }} />
                    <Typography variant="overline" color="text.secondary">Prioritization</Typography>
                    <Grid container spacing={2} sx={{ mt: 0 }}>
                      <Grid item xs={12} md={4}>
                        <TextField select fullWidth size="small" label="Confirmed severity (I–IV)" value={f.sev} onChange={(e) => setForm(t, { sev: e.target.value })}>
                          {SEVERITY_LEVELS.map((s) => (
                            <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField select fullWidth size="small" label="Priority" value={f.priority} onChange={(e) => setForm(t, { priority: e.target.value })} InputProps={{ startAdornment: <LowPriorityIcon sx={{ mr: 1, color: 'action.active' }} /> }}>
                          {PRIORITIES.map((p) => (
                            <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        {f.priority === 'P0' && (
                          <TextField type="date" fullWidth size="small" label="Due date" required InputLabelProps={{ shrink: true }} value={f.dueDate} onChange={(e) => setForm(t, { dueDate: e.target.value })} />
                        )}
                      </Grid>
                      <Grid item xs={12}>
                        <OwnerFields org={viewer.org} members={viewer.members} value={f.owner} onChange={(owner) => setForm(t, { owner })} />
                      </Grid>
                    </Grid>

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2, flexWrap: 'wrap' }}>
                      <Button variant="text" color="error" size="small" startIcon={<CloseIcon />} disabled={busy} onClick={() => setDialog({ kind: 'reject', condition: t })}>
                        Not Approved
                      </Button>
                      <Button variant="outlined" size="small" startIcon={<ReplayIcon />} disabled={busy} onClick={() => setDialog({ kind: 'return', condition: t })}>
                        Return to L1/FLS
                      </Button>
                      <Button variant="contained" size="small" startIcon={busy ? <CircularProgress size={14} color="inherit" /> : <CheckIcon />} disabled={busy || (f.priority === 'P0' && !f.dueDate)} onClick={() => confirm(t)}>
                        {busy ? 'Confirming…' : 'Confirm & Assign Priority'}
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              );
            })}
          </Stack>
        </>
      )}

      {tab === 'resolutions' && (
        <>
          {resolutions.length === 0 && <Alert severity="success">No resolutions awaiting your sign-off.</Alert>}
          <Stack spacing={2}>
            {resolutions.map((t) => {
              const busy = busyId === t.id;
              return (
                <Card key={t.id} variant="outlined">
                  <CardContent>
                    <ConditionHeader condition={t} org={viewer.org} />
                    <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>Resolution submitted</Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>{t.resolution?.submittedByName || 'Owner'}:</strong> {t.resolution?.note}
                    </Typography>
                    <Chip size="small" label={`L1 approved${t.resolution?.l1ApprovedByName ? ` by ${t.resolution.l1ApprovedByName}` : ''}`} color="success" sx={{ mb: 1.5 }} />
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                      <Button size="small" variant="outlined" color="warning" startIcon={<ReplayIcon />} disabled={busy} onClick={() => setDialog({ kind: 'return_resolution', condition: t })}>
                        Return to owner
                      </Button>
                      <Button size="small" variant="contained" color="success" startIcon={<CheckIcon />} disabled={busy} onClick={() => run(t.id, async () => { await approveResolution(t.id, { level: 'l2', ...actor }); notifyConditionEvent('resolved', t, viewer, { after: { status: 'resolved' } }); }, 'Resolved — the condition is closed.')}>
                        Approve resolution → Resolved
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              );
            })}
          </Stack>
        </>
      )}

      {tab === 'history' && (
        <>
          {history.length === 0 && <Alert severity="info">No decisions yet.</Alert>}
          <Stack spacing={1}>
            {history.map((t) => {
              const busy = busyId === t.id;
              const revisable = canRevise(t, viewer, viewer.routing) && ['approved', 'rejected'].includes(t.status);
              return (
                <Box key={t.id} sx={{ border: 1, borderColor: 'divider', borderRadius: 2, p: 1.5 }}>
                  <ConditionHeader condition={t} org={viewer.org} dense />
                  <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" alignItems="center" sx={{ mt: 1 }}>
                    {t.severityDH && <Chip label={`Confirmed: Sev ${t.severityDH}`} size="small" sx={{ bgcolor: sevColor(t.severityDH), color: '#fff', fontWeight: 700 }} />}
                    {t.priority && <Chip label={t.priority} size="small" variant="outlined" sx={{ fontWeight: 700 }} />}
                    {t.patternMatch?.confidence ? (
                      <Chip label={`Pattern match ${Math.round(t.patternMatch.confidence * 100)}%`} size="small" color="error" sx={{ fontWeight: 700 }} />
                    ) : (
                      t.status === 'approved' && <Chip label="No pattern match" size="small" variant="outlined" />
                    )}
                    <Box sx={{ flexGrow: 1 }} />
                    {revisable && (
                      <Button size="small" variant="text" startIcon={<ReplayIcon />} disabled={busy} onClick={() => setDialog({ kind: 'revise', condition: t })}>
                        Revise my decision
                      </Button>
                    )}
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        </>
      )}

      <NoteDialog
        open={dialog?.kind === 'return'}
        title="Return to L1/FLS"
        body="The item goes back to Review in Progress-L1 with your note so the L1 reviewer sees your revisions."
        label="Note to the L1 reviewer"
        confirmLabel="Return to L1"
        busy={Boolean(busyId)}
        onClose={() => setDialog(null)}
        onConfirm={(note) => run(dialog.condition.id, async () => { await dhReturnToL1(dialog.condition.id, { note, ...actor }); notifyConditionEvent('returned_to_l1', dialog.condition, viewer, { note }); }, 'Returned to L1/FLS.')}
      />
      <NoteDialog
        open={dialog?.kind === 'reject'}
        title="Not Approved"
        body="Tell the entrant and the L1 reviewer why this OE Condition is not approved."
        label="Reason"
        confirmLabel="Mark Not Approved"
        confirmColor="error"
        busy={Boolean(busyId)}
        onClose={() => setDialog(null)}
        onConfirm={(reason) => run(dialog.condition.id, async () => { await dhRejectTRL(dialog.condition.id, { reason, ...actor }); notifyConditionEvent('not_approved', dialog.condition, viewer, { note: reason }); }, 'Marked Not Approved.')}
      />
      <NoteDialog
        open={dialog?.kind === 'return_resolution'}
        title="Return the resolution to the owner"
        body="The condition goes back to Approved/Active with your note; the owner resubmits when done."
        label="What still needs to happen?"
        confirmLabel="Return"
        confirmColor="warning"
        busy={Boolean(busyId)}
        onClose={() => setDialog(null)}
        onConfirm={(note) => run(dialog.condition.id, async () => { await returnResolution(dialog.condition.id, { note, ...actor }); notifyConditionEvent('resolution_returned', dialog.condition, viewer, { note }); }, 'Resolution returned to the owner.')}
      />
      <NoteDialog
        open={dialog?.kind === 'revise'}
        title="Revise your decision"
        body="This reopens the item at L2 so you can decide again (C0814: 'can I go back and edit it?' — yes, until the next hop acts)."
        label="Why?"
        confirmLabel="Reopen at L2"
        busy={Boolean(busyId)}
        onClose={() => setDialog(null)}
        onConfirm={(note) => run(dialog.condition.id, async () => { await reviseDecision(dialog.condition.id, { toStatus: 'under_review', note, ...actor }); notifyConditionEvent('revised', dialog.condition, viewer, { note }); }, 'Reopened at L2.')}
      />
    </Box>
  );
}

export default SeverityCalibration;
